<template>
  <div class="lab-page">
    <div v-if="loading" class="lab-loading">Loading…</div>
    <div v-if="error" class="lab-error">{{ error }}</div>

    <div v-if="!loading && req" class="lab-doc">
      <!-- Header -->
      <header class="lab-header">
        <div>
          <h1 class="lab-fac-name">{{ facility.hospital_name || 'Hospital' }}</h1>
          <div class="lab-fac-sub">
            <span v-if="facility.hospital_phone">Tel: {{ facility.hospital_phone }}</span>
            <span v-if="facility.hospital_email"> · {{ facility.hospital_email }}</span>
          </div>
        </div>
        <div class="lab-doc-id">
          <div><strong>LABORATORY REPORT</strong></div>
          <div>Request #: {{ req.request_number }}</div>
          <div class="lab-muted">Issued: {{ formatDt(new Date()) }}</div>
        </div>
      </header>

      <!-- Patient block -->
      <section class="lab-section">
        <div class="lab-grid">
          <div><strong>Patient:</strong> {{ patient.name }}</div>
          <div><strong>Patient #:</strong> {{ patient.patient_no }}</div>
          <div><strong>Sex:</strong> {{ patient.gender }}</div>
          <div><strong>DOB:</strong> {{ formatDate(patient.dob) }} ({{ ageYears(patient.dob) }} yrs)</div>
          <div><strong>Requested by:</strong> {{ req.doctor_name || '—' }}</div>
          <div><strong>Request date:</strong> {{ formatDt(req.created_at) }}</div>
          <div v-if="req.priority"><strong>Priority:</strong> <span class="lab-pill">{{ req.priority }}</span></div>
          <div v-if="req.clinical_notes" class="lab-grid-full"><strong>Clinical notes:</strong> {{ req.clinical_notes }}</div>
        </div>
      </section>

      <!-- Results table -->
      <section class="lab-section">
        <h2 class="lab-h2">Results</h2>
        <table class="lab-table">
          <thead>
            <tr>
              <th>Test</th><th>Sample</th><th>Result</th><th>Unit</th>
              <th>Reference range</th><th>Flag</th><th>Verified</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(it, i) in items" :key="i" :class="flagClass(it.flag)">
              <td><strong>{{ it.test_name }}</strong></td>
              <td>{{ it.sample_type || '—' }}</td>
              <td><strong>{{ it.result_value || '—' }}</strong></td>
              <td>{{ it.unit || '' }}</td>
              <td class="lab-muted">{{ it.reference_range || '—' }}</td>
              <td>
                <span v-if="it.flag" class="lab-flag" :class="flagClass(it.flag)">{{ it.flag }}</span>
              </td>
              <td class="lab-muted">
                <div>{{ formatDt(it.verified_at) }}</div>
                <div v-if="it.verified_by_name">{{ it.verified_by_name }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Footer -->
      <footer class="lab-footer">
        <div class="lab-disclaimer">
          This report is generated electronically. Reference ranges are approximate and may vary. Discuss results with your provider.
        </div>
        <div class="lab-actions no-print">
          <button class="lab-btn" onclick="window.print()">🖨 Print</button>
          <button class="lab-btn lab-btn-secondary" @click="$router.back()">← Back</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";

export default {
  name: "PatientLabPrint",
  data() {
    return { loading: true, error: '', req: null, items: [], patient: {}, facility: {} };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      const token = localStorage.getItem("patientPortalToken");
      if (!token) { this.$router.push('/portal/login'); return; }
      try {
        const r = await axios.post(hms,
          { function: 'patientPortalLabRequestDetail', request_id: this.$route.params.id },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
        if (r.data && r.data.success) {
          this.req = r.data.data.Request;
          this.items = r.data.data.Items || [];
          this.patient = r.data.data.Patient || {};
          this.facility = r.data.data.Facility || {};
          this.loading = false;
          if (this.$route.query.auto === '1') {
            this.$nextTick(() => setTimeout(() => window.print(), 400));
          }
        } else {
          this.error = r.data?.message || 'Failed to load report.';
          this.loading = false;
        }
      } catch (e) {
        this.error = 'Network error or session expired.';
        this.loading = false;
      }
    },
    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDt(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    ageYears(dob) { if (!dob) return '?'; const d = new Date(dob); if (isNaN(d)) return '?'; return Math.floor((Date.now() - d.getTime()) / 31557600000); },
    flagClass(f) { return ({ high: 'lab-flag-warn', H: 'lab-flag-warn', low: 'lab-flag-warn', L: 'lab-flag-warn', critical_high: 'lab-flag-danger', HH: 'lab-flag-danger', critical_low: 'lab-flag-danger', LL: 'lab-flag-danger', abnormal: 'lab-flag-warn', normal: 'lab-flag-good', N: 'lab-flag-good' })[f] || ''; }
  }
};
</script>

<style scoped>
.lab-page { background: #f1f5f9; min-height: 100vh; padding: 24px; font-family: 'Helvetica Neue', sans-serif; }
.lab-loading, .lab-error { max-width: 600px; margin: 60px auto; text-align: center; color: #475569; }
.lab-error { color: #dc2626; }

.lab-doc { max-width: 850px; margin: 0 auto; background: #fff; padding: 36px 44px; box-shadow: 0 4px 14px rgba(0,0,0,.08); border-radius: 4px; color: #1e293b; }

.lab-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 18px; border-bottom: 3px solid #1a4480; margin-bottom: 24px; }
.lab-fac-name { font-size: 24px; font-weight: 800; color: #1a4480; margin: 0; }
.lab-fac-sub { color: #64748b; font-size: 12px; margin-top: 4px; }
.lab-doc-id { text-align: right; font-size: 13px; }
.lab-doc-id strong { font-size: 14px; color: #1a4480; letter-spacing: 1px; }
.lab-muted { color: #94a3b8; }

.lab-section { margin-bottom: 24px; }
.lab-h2 { font-size: 14px; font-weight: 700; color: #1a4480; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }

.lab-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 24px; font-size: 13px; }
.lab-grid-full { grid-column: 1 / -1; }

.lab-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.lab-table th { background: #f1f5f9; padding: 10px 8px; border-bottom: 2px solid #cbd5e1; text-align: left; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; }
.lab-table td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
.lab-table tr.lab-flag-warn   td { background: #fffbeb; }
.lab-table tr.lab-flag-danger td { background: #fef2f2; }

.lab-pill { display: inline-block; padding: 1px 8px; border-radius: 10px; background: #dbeafe; color: #1e3a8a; font-size: 11px; font-weight: 600; text-transform: uppercase; }
.lab-flag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.lab-flag-good   { background: #dcfce7; color: #166534; }
.lab-flag-warn   { background: #fef3c7; color: #92400e; }
.lab-flag-danger { background: #fecaca; color: #991b1b; }

.lab-footer { margin-top: 36px; padding-top: 18px; border-top: 1px solid #e2e8f0; }
.lab-disclaimer { font-size: 10px; color: #94a3b8; font-style: italic; margin-bottom: 12px; }
.lab-actions { display: flex; gap: 8px; }
.lab-btn { padding: 8px 16px; background: #1a4480; color: #fff; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 13px; }
.lab-btn-secondary { background: #fff; color: #1a4480; border: 1px solid #1a4480; }

@media print {
  .lab-page { background: #fff; padding: 0; }
  .lab-doc { box-shadow: none; max-width: none; padding: 16mm; }
  .no-print { display: none !important; }
}
</style>
